import { useSyncExternalStore } from "react";

import type { ExtensionHello } from "@punto/frontend-shared/extension";

import { type ChromeRuntime, getChromeRuntime } from "./chrome-runtime";
import { EXTENSION_ID } from "./extension-constants";

/**
 * - `unsupported`: the browser cannot run Chrome extensions.
 * - `connecting`: waiting for an answer.
 * - `connected`: the extension answered with its version, or, without one, an
 *   extension released before the port answers messages.
 * - `missing`: no answer (not installed, or disabled).
 */
export type ExtensionConnectionState =
  | "unsupported"
  | "connecting"
  | "connected"
  | "missing";

export interface ExtensionConnectionSnapshot {
  state: ExtensionConnectionState;
  /** The version the extension answered with last. */
  version?: string;
  /** True once a reconnect answered with a version other than the first one seen. */
  updated: boolean;
}

/** `navigator.userAgentData`, which TypeScript's DOM types do not declare. */
export interface UserAgentData {
  brands: { brand: string }[];
  mobile: boolean;
}

/** A reloading extension is briefly gone: the waits before each new attempt. */
const RETRY_DELAYS_MS = [1_000, 2_000, 5_000];

/**
 * Sent before giving up on the extension. No extension handles it: one that is
 * installed leaves it unanswered, and only when nothing is installed does
 * Chrome answer with this error.
 */
const PROBE_MESSAGE = { type: "probe" };
const NOTHING_RECEIVES_ERROR = "Receiving end does not exist";

const isSupportedBrowser = (data: UserAgentData | undefined): boolean =>
  data !== undefined &&
  !data.mobile &&
  data.brands.some(({ brand }) => brand === "Chromium");

/**
 * One long-lived `externally_connectable` port from this Janus tab to the
 * extension (P-2789). It does not depend on the content script, so it says
 * "installed, version X" before any click, and its disconnect says the
 * extension restarted — which also killed this tab's content script.
 */
export class ExtensionConnection {
  private snapshot: ExtensionConnectionSnapshot;
  private readonly listeners = new Set<() => void>();
  private firstVersion: string | undefined;
  private started = false;

  constructor(
    private readonly deps: {
      userAgentData: UserAgentData | undefined;
      getRuntime: () => ChromeRuntime | undefined;
    },
  ) {
    this.snapshot = {
      state: isSupportedBrowser(deps.userAgentData)
        ? "connecting"
        : "unsupported",
      updated: false,
    };
  }

  /** For `useSyncExternalStore`; the first subscriber opens the port. */
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    this.start();
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = (): ExtensionConnectionSnapshot => this.snapshot;

  /** True once the extension answers with a version other than `version`; false after `timeoutMs`. */
  waitForVersionOtherThan(
    version: string | undefined,
    timeoutMs: number,
  ): Promise<boolean> {
    const hasOtherVersion = () =>
      this.snapshot.state === "connected" && this.snapshot.version !== version;
    if (hasOtherVersion()) return Promise.resolve(true);

    return new Promise((resolve) => {
      const stop = (result: boolean) => {
        clearTimeout(timer);
        this.listeners.delete(onChange);
        resolve(result);
      };
      const onChange = () => {
        if (hasOtherVersion()) stop(true);
      };
      const timer = setTimeout(() => stop(false), timeoutMs);
      this.listeners.add(onChange);
    });
  }

  private start(): void {
    if (this.started || this.snapshot.state === "unsupported") return;
    this.started = true;
    this.open([]);
  }

  /**
   * Opens a port. `retryDelays` is what is left to try if it closes without an
   * answer: nothing for the first port, since an extension that never
   * answered is not there. A port that did answer starts the retries over.
   */
  private open(retryDelays: readonly number[]): void {
    const runtime = this.deps.getRuntime();
    if (!runtime?.connect) {
      this.update({ state: "missing" });
      return;
    }

    let answered = false;
    const port = runtime.connect(EXTENSION_ID);
    port.onMessage.addListener((message) => {
      answered = true;
      this.onAnswer((message as ExtensionHello).version);
    });
    port.onDisconnect.addListener(() => {
      // Read it, or Chrome logs "Unchecked runtime.lastError".
      void runtime.lastError;
      const [delay, ...rest] = answered ? RETRY_DELAYS_MS : retryDelays;
      if (delay === undefined) {
        this.probe(runtime);
        return;
      }
      this.update({ state: "connecting" });
      setTimeout(() => this.open(rest), delay);
    });
  }

  /**
   * An extension released before this port existed closes it unanswered, just
   * as when nothing is installed, yet it still opens the side panel. A message
   * tells the two apart: that extension leaves it unanswered, while with
   * nothing installed Chrome reports that nothing receives it.
   */
  private probe(runtime: ChromeRuntime): void {
    if (!runtime.sendMessage) {
      this.update({ state: "missing" });
      return;
    }
    runtime.sendMessage(EXTENSION_ID, PROBE_MESSAGE, () => {
      const error = runtime.lastError?.message ?? "";
      this.update(
        error.includes(NOTHING_RECEIVES_ERROR)
          ? { state: "missing" }
          : { state: "connected", version: undefined },
      );
    });
  }

  private onAnswer(version: string): void {
    this.firstVersion ??= version;
    this.update({
      state: "connected",
      version,
      updated: this.snapshot.updated || version !== this.firstVersion,
    });
  }

  private update(patch: Partial<ExtensionConnectionSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...patch };
    this.listeners.forEach((listener) => listener());
  }
}

/** This tab's connection. */
export const extensionConnection = new ExtensionConnection({
  userAgentData: (navigator as Navigator & { userAgentData?: UserAgentData })
    .userAgentData,
  getRuntime: getChromeRuntime,
});

/** This tab's connection to the extension, opened on first use. Synchronous, so a click keeps its user gesture. */
export const useExtensionConnection = (): ExtensionConnectionSnapshot =>
  useSyncExternalStore(
    extensionConnection.subscribe,
    extensionConnection.getSnapshot,
  );
